import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { alpha } from '@mui/material/styles';
import { useTenantStore } from '../../stores/tenant.store';
import {
  socraticApi,
  type SocraticSessionDto,
  type SocraticCoverage,
} from '../../lib/api/socratic.api';
import { formatDate } from '../../lib/i18n-format';
import { errorText } from '../../lib/error-text';

type Filter = 'all' | 'toReview' | 'reviewed';

/** A session enriched with the student's resolved display name. */
interface Row extends SocraticSessionDto {
  studentName: string;
}

// Integrated teacher review of a socratic assignment — mirrors the essay grader's
// 3-pane shape (roster | transcript | work panel), but socratic is FORMATIVE
// (decision 3): NO numeric grade. The "grade" surface is the AI coverage + prose
// plus the teacher's own formative note. Only closed/evaluated sessions (decision
// 11 — never a live view).
export function SocraticReviewPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { currentTenantId } = useTenantStore();

  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [rosterOpen, setRosterOpen] = useState(true);

  useEffect(() => {
    if (!currentTenantId || !assignmentId) return;
    let alive = true;
    (async () => {
      try {
        // El servidor trae sólo lo terminado, con el nombre (PR 3A): antes se
        // leían `socraticSessions` y `people` directo de Firestore.
        const finished = await socraticApi.listSessionsForAssignment(currentTenantId, assignmentId);
        if (!alive) return;
        const enriched: Row[] = finished
          .map((s) => ({
            ...s,
            studentName:
              s.studentName || t('socraticReview.student', { uid: s.studentUid.slice(0, 8) }),
          }))
          .sort((a, b) => a.studentName.localeCompare(b.studentName));
        setRows(enriched);
        setSelectedId((prev) => prev ?? enriched[0]?.id ?? null);
      } catch (e) {
        // EL MOTIVO DEL SERVIDOR, traducido: «no autorizado para esta tarea»
        // dice algo que «no se pudo cargar» calla.
        if (alive) setError(errorText(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentTenantId, assignmentId, t]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    if (filter === 'toReview') return rows.filter((r) => !r.reviewedAt);
    if (filter === 'reviewed') return rows.filter((r) => !!r.reviewedAt);
    return rows;
  }, [rows, filter]);

  const selected = useMemo(
    () => rows?.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  );

  // Replace one row in place after an evaluate / note save.
  const patchRow = (next: SocraticSessionDto) => {
    setRows((prev) => (prev ?? []).map((r) => (r.id === next.id ? { ...r, ...next } : r)));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 1, color: 'text.secondary' }}
      >
        {t('socraticReview.back')}
      </Button>
      <Typography variant="h5" fontWeight={700}>
        {t('socraticReview.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('socraticReview.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {rows === null && !error && (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      )}

      {rows !== null && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('socraticReview.empty')}
        </Typography>
      )}

      {rows !== null && rows.length > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          {rosterOpen ? (
            <Roster
              rows={filtered}
              total={rows.length}
              selectedId={selectedId}
              filter={filter}
              onFilter={setFilter}
              onPick={setSelectedId}
              onCollapse={() => setRosterOpen(false)}
            />
          ) : (
            <IconButton
              onClick={() => setRosterOpen(true)}
              sx={{ display: { xs: 'none', md: 'inline-flex' }, mt: 1 }}
              aria-label={t('socraticReview.rosterTitle')}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          )}

          {selected ? (
            <>
              <TranscriptPane row={selected} />
              <WorkPane row={selected} onPatch={patchRow} />
            </>
          ) : (
            <Paper variant="outlined" sx={{ flex: 1, p: 4, borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('socraticReview.selectPrompt')}
              </Typography>
            </Paper>
          )}
        </Stack>
      )}
    </Box>
  );
}

// ─── Left: roster ─────────────────────────────────────────────────────────────

function Roster({
  rows,
  total,
  selectedId,
  filter,
  onFilter,
  onPick,
  onCollapse,
}: {
  rows: Row[];
  total: number;
  selectedId: string | null;
  filter: Filter;
  onFilter: (f: Filter) => void;
  onPick: (id: string) => void;
  onCollapse: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const filters: Filter[] = ['all', 'toReview', 'reviewed'];
  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: '100%', md: 320 },
        flexShrink: 0,
        borderRadius: 2,
        position: { md: 'sticky' },
        top: { md: 16 },
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {t('socraticReview.rosterTitle')} ({total})
        </Typography>
        <IconButton
          size="small"
          onClick={onCollapse}
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
        >
          <ChevronLeftRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Stack direction="row" spacing={0.5} sx={{ px: 1.5, pb: 1, flexWrap: 'wrap' }} useFlexGap>
        {filters.map((f) => (
          <Chip
            key={f}
            size="small"
            label={t(`socraticReview.filter.${f}`)}
            color={filter === f ? 'primary' : 'default'}
            variant={filter === f ? 'filled' : 'outlined'}
            onClick={() => onFilter(f)}
          />
        ))}
      </Stack>
      <Divider />
      <Stack sx={{ maxHeight: { md: '70vh' }, overflowY: 'auto' }}>
        {rows.map((r, i) => {
          const active = r.id === selectedId;
          return (
            <Box
              key={r.id}
              onClick={() => onPick(r.id)}
              sx={{
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                borderLeft: '3px solid',
                borderLeftColor: active ? 'primary.main' : 'transparent',
                bgcolor: active ? (th) => alpha(th.palette.primary.main, 0.08) : 'transparent',
                '&:hover': { bgcolor: (th) => alpha(th.palette.action.hover, 0.6) },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 18 }}>
                  {i + 1}.
                </Typography>
                <Typography variant="body2" noWrap fontWeight={active ? 700 : 400} sx={{ flex: 1 }}>
                  {r.studentName}
                </Typography>
                {r.reviewedAt && <CheckCircleRoundedIcon color="success" sx={{ fontSize: 16 }} />}
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, pl: 3 }}>
                <Chip
                  size="small"
                  label={t(`socraticReview.status.${r.status}`)}
                  color={r.status === 'evaluated' ? 'success' : 'default'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11 }}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('socraticReview.attemptN', { n: r.attemptNumber })}
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

// ─── Center: transcript ───────────────────────────────────────────────────────

function TranscriptPane({ row }: { row: Row }): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Paper variant="outlined" sx={{ flex: 1, borderRadius: 2, p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {row.studentName}
        </Typography>
        <Chip
          size="small"
          label={t(`socraticReview.status.${row.status}`)}
          color={row.status === 'evaluated' ? 'success' : 'default'}
          variant="outlined"
        />
        <Chip
          size="small"
          variant="outlined"
          label={t('socraticReview.attemptN', { n: row.attemptNumber })}
        />
      </Stack>
      <Divider sx={{ mb: 1.5 }} />
      {row.turns.map((turn) => (
        <Box key={turn.index} sx={{ mb: 2 }}>
          <Typography variant="caption" color="primary" fontWeight={700}>
            {t('socraticReview.q')}
          </Typography>
          <Typography variant="body2" sx={{ mb: turn.answer !== null ? 1 : 0 }}>
            {turn.question}
          </Typography>
          {turn.answer !== null && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t('socraticReview.a')}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {turn.answer}
              </Typography>
            </>
          )}
        </Box>
      ))}
    </Paper>
  );
}

// ─── Right: work panel (coverage / feedback / teacher note) ───────────────────

type WorkTab = 'coverage' | 'feedback' | 'note';

function WorkPane({
  row,
  onPatch,
}: {
  row: Row;
  onPatch: (next: SocraticSessionDto) => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [tab, setTab] = useState<WorkTab>('coverage');
  const [note, setNote] = useState(row.teacherNote ?? '');
  const [savingNote, setSavingNote] = useState(false);
  const [noteMsg, setNoteMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  const [evalErr, setEvalErr] = useState<string | null>(null);

  // Reset the note draft when switching students.
  useEffect(() => {
    setNote(row.teacherNote ?? '');
    setNoteMsg(null);
    setEvalErr(null);
  }, [row.id, row.teacherNote]);

  const runEvaluate = async () => {
    if (!currentTenantId) return;
    setEvaluating(true);
    setEvalErr(null);
    try {
      const { session } = await socraticApi.evaluate({
        tenantId: currentTenantId,
        sessionId: row.id,
      });
      onPatch(session);
    } catch {
      setEvalErr(t('socraticReview.evaluateError'));
    } finally {
      setEvaluating(false);
    }
  };

  const saveNote = async () => {
    if (!currentTenantId) return;
    setSavingNote(true);
    setNoteMsg(null);
    try {
      const { session } = await socraticApi.saveTeacherNote({
        tenantId: currentTenantId,
        sessionId: row.id,
        note,
      });
      onPatch(session);
      setNoteMsg({ kind: 'ok', text: t('socraticReview.saved') });
    } catch {
      setNoteMsg({ kind: 'err', text: t('socraticReview.saveNoteError') });
    } finally {
      setSavingNote(false);
    }
  };

  const reviewedLabel = row.reviewedAt
    ? t('socraticReview.reviewedAt', {
        date: formatDate(row.reviewedAt),
      })
    : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: '100%', md: 380 },
        flexShrink: 0,
        borderRadius: 2,
        position: { md: 'sticky' },
        top: { md: 16 },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5 }}>
        {reviewedLabel ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            icon={<CheckCircleRoundedIcon />}
            label={reviewedLabel}
            sx={{ flex: 1, justifyContent: 'flex-start' }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {t('socraticReview.formativeHint')}
          </Typography>
        )}
        <Button
          size="small"
          variant="contained"
          startIcon={
            evaluating ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeRoundedIcon />
          }
          onClick={runEvaluate}
          disabled={evaluating}
        >
          {evaluating
            ? t('socraticReview.evaluating')
            : row.evaluation
              ? t('socraticReview.reEvaluate')
              : t('socraticReview.evaluate')}
        </Button>
      </Stack>
      {evalErr && (
        <Alert severity="error" sx={{ mx: 1.5, mb: 1 }}>
          {evalErr}
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v as WorkTab)} variant="fullWidth">
        <Tab value="coverage" label={t('socraticReview.tabs.coverage')} />
        <Tab value="feedback" label={t('socraticReview.tabs.feedback')} />
        <Tab value="note" label={t('socraticReview.tabs.note')} />
      </Tabs>
      <Divider />
      <Box sx={{ p: 2 }}>
        {tab === 'coverage' &&
          (row.evaluation ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {row.evaluation.coverageMap.map((ax) => (
                <CoverageChip
                  key={ax.axisId}
                  axisId={ax.axisId}
                  coverage={ax.coverage}
                  {...(ax.label ? { label: ax.label } : {})}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('socraticReview.noEvaluation')}
            </Typography>
          ))}

        {tab === 'feedback' &&
          (row.evaluation?.prose ? (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {row.evaluation.prose}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('socraticReview.noFeedback')}
            </Typography>
          ))}

        {tab === 'note' && (
          <Stack spacing={1.5}>
            <TextField
              multiline
              minRows={6}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('socraticReview.notePlaceholder')}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              {t('socraticReview.noteHint')}
            </Typography>
            {noteMsg && (
              <Alert severity={noteMsg.kind === 'ok' ? 'success' : 'error'}>{noteMsg.text}</Alert>
            )}
            <Button
              variant="contained"
              onClick={saveNote}
              disabled={savingNote}
              startIcon={savingNote ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {savingNote
                ? t('socraticReview.saving')
                : row.reviewedAt
                  ? t('socraticReview.saveNote')
                  : t('socraticReview.markReviewed')}
            </Button>
          </Stack>
        )}
      </Box>
    </Paper>
  );
}

function CoverageChip({
  axisId,
  coverage,
  label,
}: {
  axisId: string;
  coverage: SocraticCoverage;
  label?: string;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const tone: Record<SocraticCoverage, string> = {
    covered: '#2f6f4f',
    partial: '#b5852f',
    untouched: '#8a7fa6',
  };
  const name = label ? t(label) : axisId;
  return (
    <Chip
      size="small"
      label={`${name} · ${t(`socraticReview.coverage.${coverage}`)}`}
      sx={{ bgcolor: alpha(tone[coverage], 0.14), color: tone[coverage], fontWeight: 600 }}
    />
  );
}
